import { useState, useEffect, useMemo } from 'react';

// Lightive foundation date: May 8, 2018, 09:00 KST
const FOUNDED = new Date('2018-05-08T09:00:00+09:00');

interface ElapsedTime {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalDays: number;
}

function getElapsed(): ElapsedTime {
  const now = new Date();
  const diffMs = now.getTime() - FOUNDED.getTime();
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Calculate years, months, days
  let years = now.getFullYear() - FOUNDED.getFullYear();
  let months = now.getMonth() - FOUNDED.getMonth();
  let days = now.getDate() - FOUNDED.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  // Calculate hours, minutes, seconds from the remaining time today
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();

  return { years, months, days, hours, minutes, seconds, totalDays };
}

type SinceFoundationVariant = 'compact' | 'detailed' | 'dynamic' | 'live';

interface SinceFoundationProps {
  /** Display variant. Default: 'compact' */
  variant?: SinceFoundationVariant;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Displays elapsed time since Lightive's foundation (May 8, 2018).
 *
 * Variants:
 * - compact: "Since 2018 · 7+ Years"
 * - detailed: "2,500+ Days of Craft"
 * - dynamic: "7Y 8M 15D" (updates daily)
 * - live: "7Y 03M 15D 12H 34M 56S" (updates every second with blinking animation)
 */
export function SinceFoundation({
  variant = 'compact',
  className = '',
}: SinceFoundationProps) {
  const [elapsed, setElapsed] = useState<ElapsedTime>(getElapsed);

  // Update based on variant
  useEffect(() => {
    if (variant === 'live') {
      // Update every second for live variant
      const interval = setInterval(() => {
        setElapsed(getElapsed());
      }, 1000);
      return () => clearInterval(interval);
    }

    if (variant === 'compact') return;

    // Calculate ms until next midnight for other variants
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    // Initial timeout until midnight
    const timeout = setTimeout(() => {
      setElapsed(getElapsed());

      // Then update daily
      const interval = setInterval(() => {
        setElapsed(getElapsed());
      }, 24 * 60 * 60 * 1000);

      return () => clearInterval(interval);
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  }, [variant]);

  const displayText = useMemo(() => {
    switch (variant) {
      case 'compact':
        return `Since 2018 · ${elapsed.years}+ Years`;
      case 'detailed':
        return `${elapsed.totalDays.toLocaleString()}+ Days of Craft`;
      case 'dynamic':
        return `${elapsed.years}Y ${elapsed.months}M ${elapsed.days}D`;
      case 'live':
        // Will be handled separately with animated rendering
        return '';
      default:
        return `Since 2018`;
    }
  }, [variant, elapsed]);

  // Live variant
  // Format: "SINCE 2828 DAYS"
  if (variant === 'live') {
    return (
      <span
        className={`font-en text-xs tracking-wider font-bold uppercase ${className}`}
        title={`Founded: May 8, 2018`}
      >
        SINCE {elapsed.totalDays.toLocaleString()} DAYS
      </span>
    );
  }

  return (
    <span
      className={`font-en text-xs tracking-wide text-white/60 ${className}`}
      title={`Founded: May 8, 2018`}
    >
      {displayText}
    </span>
  );
}
